import { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import Eyebrow from "@/components/Eyebrow";

const heroImg =
  "https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";

// image = Unsplash placeholder, swap for real Ecospora photography later
const tracks = [
  {
    id: "b2b",
    label: "B2B Partnership",
    body: "Supply partnerships and joint ventures. Reliable beglog and bulk mushroom supply for growers, retailers, and food producers, coordinated through Ecospora's central operations.",
    headline: "Supply you can build a business on.",
    bullets: [
      "Consistent beglog and bulk mushroom supply",
      "Central coordination and guaranteed offtake",
      "Joint ventures on new product lines",
    ],
    cta: "Start a supply conversation →",
    image:
      "https://images.unsplash.com/photo-1776918570415-52e7f702802a?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
  {
    id: "agency",
    label: "Agency",
    body: "Bring Ecospora and our sub-brands to your market. Defined territories, tiered margin, onboarding, and marketing support, built for reseller networks and distribution partners.",
    headline: "Bring Ecospora to your market.",
    bullets: [
      "Defined territories and tiered margin",
      "Onboarding and marketing support",
      "Access to the sub-brand portfolio",
    ],
    cta: "Explore an agency partnership →",
    image:
      "https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
  {
    id: "university",
    label: "University",
    body: "Research partnerships and joint R&D. We bring the operating system and a live facility; you bring the lab. Active pipelines already run with USM and UPM.",
    headline: "Research with a live system.",
    bullets: [
      "Joint R&D and commercialisation paths",
      "Access to a live facility and its data",
      "Active pipelines with USM and UPM",
    ],
    cta: "Start a research conversation →",
    image:
      "https://images.unsplash.com/photo-1755606045126-1aeb4f0e7c2d?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
];

const includedItems = [
  { title: "R&D & Partnership", body: "Joint research and long-term partnership, structured around real outcomes." },
  { title: "Consultation", body: "Direct access to our team and operating know-how across the mushroom value chain." },
  { title: "Future Innovation", body: "A seat in what we build next, from mycelium materials to new product categories." },
];

const gainItems = [
  { title: "Grant credibility", body: "A government-backed, university-validated model that strengthens grant applications." },
  { title: "Investor credibility", body: "A structured, ESG-aligned operating model that stands up to diligence." },
  { title: "Government trust", body: "Established relationships with LKTN, RISDA, FAMA, and Jabatan Pertanian." },
];

const whyNow = [
  { lead: "Sustainability is becoming non-negotiable.", body: "Agricultural emissions are under scrutiny and ESG-aligned procurement is reaching mid-market. We turn mushroom mycelium into sustainable packaging that replaces plastic at the source." },
  { lead: "Mushroom demand is rising.", body: "Health trends, plant-based diets, and specialty cuisine are driving demand across Malaysia and the region. We promote local mushrooms and add value to the domestic mushroom industry." },
  { lead: "Circular models are becoming the standard.", body: "Buyers, partners, and government programmes increasingly favour businesses where commercial return and community return are structurally linked." },
];

const whereWeAre = [
  "Government partnership with LKTN, with beglog supply contracts in place and the asnaf network operational.",
  "Active R&D pipelines with two top-tier Malaysian universities, at least one moving toward pilot commercialisation.",
  "Multi-channel revenue across B2B beglog supply, B2B fresh and dried mushroom supply, and B2C food products through sub-brands.",
  "Operational depth in Perlis with nationwide reach for dried mushrooms and beglog.",
  "Five circular loops in operation or development, three running today and two building with USM and UPM. Circularity is the operating principle.",
];

const moatItems = [
  { title: "We own the whole loop", body: "We own our kenaf, beglog, seed, and harvest. That is supply control competitors have to rent." },
  { title: "Institutionally validated", body: "Research partnerships with USM, UPM, UniMAP, and UUM, alongside government bodies LKTN, RISDA, FAMA, and Jabatan Pertanian." },
  { title: "An inclusive workforce is a structural strength", body: "OKU workers, single mothers, elderly members, and asnaf families make the model grant-eligible and socially licensed." },
  { title: "Kenaf-based beglog", body: "A higher-yield substrate that outperforms standard sawdust blocks." },
];

const Collaborate = () => {
  const [activeTrack, setActiveTrack] = useState(tracks[0].id);
  const active = tracks.find((t) => t.id === activeTrack) || tracks[0];

  return (
    <div data-testid="collaborate-page">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Business partnership handshake" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/30 mix-blend-multiply" />
        </div>
        <div className="relative container-x pb-20 md:pb-28 pt-32">
          <div className="max-w-3xl">
            <Eyebrow color="ochre" className="mb-8">
              <span className="text-ecospora-ochre">Collaborate</span>
            </Eyebrow>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[72px] font-bold leading-[1.05] tracking-tight text-ecospora-cream text-balance">
              Build with a brand <span className="italic font-light text-ecospora-green-soft">worth building on.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ecospora-cream/85 max-w-2xl leading-relaxed">
              Ecospora works with B2B partners, agencies, and universities who want to build on a mushroom platform with real operating depth. Wherever you fit, we'd welcome a conversation.
            </p>
            <div className="mt-10">
              <Link to="/contact#form" className="btn-ochre" data-testid="collaborate-hero-cta">
                Start a conversation →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TABS */}
      <section className="section-y bg-ecospora-cream">
        <div className="container-x">
          <Reveal className="max-w-3xl mb-12">
            <Eyebrow className="mb-6">Three Ways to Collaborate</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
              Three ways to <span className="italic font-light">collaborate.</span>
            </h2>
          </Reveal>

          <Reveal className="flex flex-wrap gap-3 mb-10" role="tablist" aria-label="Collaboration tracks">
            {tracks.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`collab-tab-${t.id}`}
                aria-selected={activeTrack === t.id}
                aria-controls="collab-tabpanel"
                onClick={() => setActiveTrack(t.id)}
                data-testid={`collab-tab-${t.id}`}
                className={`px-7 py-3.5 rounded-full text-sm font-semibold uppercase tracking-[0.08em] transition-colors border ${
                  activeTrack === t.id
                    ? "bg-ecospora-green-deep text-ecospora-cream border-ecospora-green-deep"
                    : "bg-transparent text-ecospora-charcoal border-ecospora-green-deep/25 hover:border-ecospora-green-deep/50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </Reveal>

          <Reveal>
            <div
              id="collab-tabpanel"
              role="tabpanel"
              aria-labelledby={`collab-tab-${active.id}`}
              data-testid="collab-tab-panel"
              className="grid md:grid-cols-2 gap-8 md:gap-12 items-center"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  key={active.id}
                  src={active.image}
                  alt={active.label}
                  className="w-full h-full object-cover animate-fade-in"
                />
              </div>
              <div>
                <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-ecospora-ochre">
                  {active.label}
                </span>
                <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-ecospora-green-deep mt-4 leading-tight">
                  {active.headline}
                </h3>
                <p className="mt-5 text-lg text-ecospora-charcoal/80 leading-[1.7]">{active.body}</p>
                <ul className="mt-6 space-y-3">
                  {active.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-ecospora-green-deep flex items-center justify-center shrink-0 mt-0.5">
                        <Check size={13} strokeWidth={2} className="text-ecospora-ochre" />
                      </div>
                      <span className="text-ecospora-charcoal/85 leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact#form" className="btn-ochre mt-8 inline-flex">
                  {active.cta}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="section-y bg-ecospora-white">
        <div className="container-x">
          <Reveal className="max-w-3xl mb-16">
            <Eyebrow className="mb-6">What's Included</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
              The promise of the collaboration.
            </h2>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-3 gap-6">
            {includedItems.map((item, i) => (
              <div key={item.title} data-testid={`included-${i}`} className="p-8 rounded-2xl bg-ecospora-cream border border-ecospora-green-deep/10">
                <div className="font-display text-4xl text-ecospora-ochre">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-2xl font-semibold tracking-tight text-ecospora-green-deep mt-6">
                  {item.title}
                </h3>
                <p className="mt-4 text-ecospora-charcoal/75 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* WHAT PARTNERS GAIN */}
      <section className="section-y bg-ecospora-cream">
        <div className="container-x">
          <Reveal className="max-w-3xl mb-16">
            <Eyebrow className="mb-6">What Partners Gain</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
              Credibility that compounds.
            </h2>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-3 gap-6">
            {gainItems.map((item, i) => (
              <div key={item.title} data-testid={`gain-${i}`} className="p-8 rounded-2xl bg-ecospora-white border border-ecospora-green-deep/10">
                <h3 className="font-display text-2xl font-semibold tracking-tight text-ecospora-green-deep">
                  {item.title}
                </h3>
                <p className="mt-4 text-ecospora-charcoal/75 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* OUR THESIS */}
      <section className="section-y bg-ecospora-white">
        <div className="container-x">
          <Reveal className="max-w-3xl mx-auto text-center">
            <Eyebrow className="justify-center inline-flex mb-6">Our Thesis</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
              Mushrooms are a platform. <span className="italic font-light">The entry point to many categories.</span>
            </h2>
            <div className="mt-10 space-y-6 text-lg text-ecospora-charcoal/80 leading-[1.7]">
              <p>
                A spore is the beginning of food. It is also the beginning of fertiliser, packaging, construction materials, and substitutes for petrochemical products.
              </p>
              <p>
                Ecospora is a vertically-integrated mushroom company built on sustainable inputs (kenaf), circular outputs (compost, packaging), and inclusive operations (asnaf grower network). That system is the natural platform to expand into adjacent material categories.
              </p>
              <p className="font-display text-2xl text-ecospora-green-deep italic pt-4">
                The mushroom is the entry point. <span className="not-italic font-semibold">The platform is the prize.</span>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY NOW */}
      <section className="section-y bg-ecospora-cream">
        <div className="container-x">
          <Reveal className="grid md:grid-cols-12 gap-10 items-start mb-16">
            <div className="md:col-span-5">
              <Eyebrow className="mb-6">Why Now</Eyebrow>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
                A market shift is happening. <span className="italic font-light">We're built for it.</span>
              </h2>
            </div>
            <div className="md:col-span-7">
              <p className="text-lg text-ecospora-charcoal/80 leading-[1.7]">
                Three forces are converging in Southeast Asia:
              </p>
            </div>
          </Reveal>

          <Reveal stagger className="space-y-10 md:space-y-12 max-w-4xl">
            {whyNow.map((p, i) => (
              <div key={p.lead} className="grid md:grid-cols-12 gap-6 items-baseline" data-testid={`why-now-${i}`}>
                <div className="md:col-span-1 font-display text-3xl text-ecospora-ochre">{String(i + 1).padStart(2, "0")}</div>
                <div className="md:col-span-11 text-lg text-ecospora-charcoal/85 leading-[1.7]">
                  <strong className="text-ecospora-green-deep">{p.lead}</strong> {p.body}
                </div>
              </div>
            ))}
          </Reveal>

          <Reveal className="mt-16 max-w-3xl">
            <p className="font-display text-2xl md:text-3xl text-ecospora-green-deep italic leading-[1.3]">
              Ecospora sits at the intersection of all three, <span className="not-italic font-semibold">with a head start.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHERE WE ARE */}
      <section className="section-y bg-ecospora-white">
        <div className="container-x">
          <Reveal className="max-w-3xl mb-12">
            <Eyebrow className="mb-6">Where We Are</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
              Built quietly. <span className="italic font-light">Ready to scale.</span>
            </h2>
            <p className="mt-6 text-lg text-ecospora-charcoal/80 leading-[1.7]">
              Here is the picture in brief:
            </p>
          </Reveal>

          <Reveal stagger className="space-y-5 max-w-4xl">
            {whereWeAre.map((t, i) => (
              <div key={i} data-testid={`where-${i}`} className="flex items-start gap-5 p-6 rounded-xl bg-ecospora-cream/60 border border-ecospora-green-deep/10">
                <div className="w-9 h-9 rounded-full bg-ecospora-green-deep flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={16} strokeWidth={2} className="text-ecospora-ochre" />
                </div>
                <p className="text-lg text-ecospora-charcoal/85 leading-relaxed">{t}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* OUR MOAT */}
      <section className="section-y bg-ecospora-cream">
        <div className="container-x">
          <Reveal className="max-w-3xl mb-16">
            <Eyebrow className="mb-6">Our Moat</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
              Hard to replicate. <span className="italic font-light">By design.</span>
            </h2>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {moatItems.map((item, i) => (
              <div key={item.title} data-testid={`moat-${i}`} className="p-8 rounded-2xl bg-ecospora-white border border-ecospora-green-deep/10 flex flex-col">
                <span className="font-display text-3xl font-bold text-ecospora-ochre tracking-tight leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display text-xl font-semibold tracking-tight text-ecospora-green-deep mt-6">
                  {item.title}
                </h3>
                <p className="mt-4 text-[15px] text-ecospora-charcoal/75 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-ecospora-green-deep py-24 md:py-32">
        <div className="container-x">
          <Reveal className="max-w-2xl">
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-cream leading-[1.05]">
              Let's build something that <span className="italic font-light text-ecospora-green-soft">lasts.</span>
            </h2>
            <div className="mt-10">
              <Link to="/contact#form" className="btn-ochre" data-testid="collaborate-closing-cta">
                Start a conversation →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Collaborate;
